import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ActiveJobDto, TemplateDto } from '@chat-emp/shared';

import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { getActiveJobs } from '../lib/facade.js';
import { fillTemplate, INFO_TOPICS, templateNeeds, type FillContext } from './templateFill.js';
import styles from './TemplatePanel.module.css';

interface TemplatePanelProps {
  templates: TemplateDto[];
  loading: boolean;
  error: string | null;
  canCreateMore: boolean;
  onFetch: () => void;
  /** The chat the template lands in; fills its bracketed tokens. */
  context: FillContext;
  /** Receives the filled text, never raw tokens. */
  onUse: (t: TemplateDto, text: string) => void;
  /** Sends the suggested role as a card; omitted, only its title is filled in. */
  onSendJob?: (jobId: string) => Promise<void>;
  /** Resolves to the failure message, or null once saved. */
  onCreate: (name: string, template: string) => Promise<string | null>;
  onUpdate: (id: string, name: string, template: string) => Promise<string | null>;
  onDelete: (id: string) => Promise<boolean>;
  onClose: () => void;
}

type PanelView = 'list' | 'create' | 'edit' | 'fill';

const NAME_MAX = 100;
const TEMPLATE_MAX = 1000;

/**
 * The composer's message templates, as a centered modal — mirrors stream-chat's
 * TemplatePanel 1:1 (list with per-template Use/Edit/Delete, a create/edit form,
 * and an in-panel delete confirmation). Presentational: CRUD is the caller's
 * (useTemplates) via the on* props. System templates are read-only.
 */
export function TemplatePanel({
  templates,
  loading,
  error,
  canCreateMore,
  onFetch,
  context,
  onUse,
  onSendJob,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
}: TemplatePanelProps) {
  const [view, setView] = useState<PanelView>('list');
  const [editingTemplate, setEditingTemplate] = useState<TemplateDto | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; template: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch once when the panel opens (mount). onFetch is stable (useCallback).
  useEffect(() => {
    onFetch();
  }, [onFetch]);

  // Close the open kebab menu on any click outside it.
  useEffect(() => {
    if (menuOpenId === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      const path = e.composedPath();
      if (menuRef.current && !path.includes(menuRef.current)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenId]);

  const saveForm = async (save: () => Promise<string | null>) => {
    setSaving(true);
    setFormError(null);
    const failure = await save();
    setSaving(false);
    setFormError(failure);
    return failure === null;
  };

  const handleCreate = async (name: string, template: string) => {
    if (!(await saveForm(() => onCreate(name, template)))) return;
    setDraft(null);
    setView('list');
  };

  const handleUpdate = async (name: string, template: string) => {
    if (!editingTemplate) return;
    if (!(await saveForm(() => onUpdate(editingTemplate.id, name, template)))) return;
    setEditingTemplate(null);
    setView('list');
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;
    const ok = await onDelete(deletingTemplate.id);
    if (ok) setDeletingTemplate(null);
  };

  const handleEditClick = (t: TemplateDto) => {
    setFormError(null);
    setMenuOpenId(null);
    setEditingTemplate(t);
    setView('edit');
  };

  const handleDeleteClick = (t: TemplateDto) => {
    setMenuOpenId(null);
    setDeletingTemplate(t);
  };

  const handleBackToList = () => {
    setFormError(null);
    setView('list');
    setEditingTemplate(null);
    setDraft(null);
  };

  const handleCopyClick = (t: TemplateDto) => {
    setMenuOpenId(null);
    setFormError(null);
    setDraft({ name: copyName(t.name, userTemplates), template: t.template });
    setView('create');
  };

  const handleUse = (t: TemplateDto) => {
    const needs = templateNeeds(t.template, context);
    if (!needs.job && !needs.otherJob && !needs.topic) {
      onUse(t, fillTemplate(t.template, context));
      return;
    }
    setEditingTemplate(t);
    setView('fill');
  };

  const headingId = useId();
  const userTemplates = templates.filter((t) => !t.isSystem);
  const systemTemplates = templates.filter((t) => t.isSystem);

  const renderCard = (t: TemplateDto, isUser: boolean) => (
    <div key={t.id} className={styles.item}>
      <div className={styles.itemHead}>
        <div className={styles.itemName}>{t.name}</div>
        <div className={styles.itemActions}>
          <button type="button" className={styles.useBtn} onClick={() => handleUse(t)}>
            Use Template
          </button>
          <div className={styles.kebab} ref={menuOpenId === t.id ? menuRef : undefined}>
            <button
              type="button"
              className={styles.kebabBtn}
              aria-label="Template actions"
              onClick={() => setMenuOpenId(menuOpenId === t.id ? null : t.id)}
            >
              <Icon name="more" size={18} />
            </button>
            {menuOpenId === t.id && (
              <div className={styles.kebabMenu}>
                {isUser ? (
                  <>
                    <button
                      type="button"
                      className={styles.kebabItem}
                      onClick={() => handleEditClick(t)}
                    >
                      <Icon name="pencil" size={16} />
                      Edit Template
                    </button>
                    <button
                      type="button"
                      className={`${styles.kebabItem} ${styles.kebabItemDanger}`}
                      onClick={() => handleDeleteClick(t)}
                    >
                      <Icon name="trash" size={16} />
                      Delete Template
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={styles.kebabItem}
                    disabled={!canCreateMore}
                    onClick={() => handleCopyClick(t)}
                  >
                    <Icon name="copy" size={16} />
                    <span className={styles.kebabItemText}>
                      Copy to my templates
                      {!canCreateMore && (
                        <span className={styles.kebabItemHint}>Delete a template to make room</span>
                      )}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <TemplatePreview text={t.template} />
    </div>
  );

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Message templates">
        {(view === 'create' || view === 'edit') && (
          <TemplateForm
            mode={view}
            initialName={(view === 'create' ? draft?.name : editingTemplate?.name) ?? ''}
            initialTemplate={
              (view === 'create' ? draft?.template : editingTemplate?.template) ?? ''
            }
            saving={saving}
            error={formError}
            onSave={view === 'create' ? handleCreate : handleUpdate}
            onCancel={handleBackToList}
          />
        )}

        {view === 'fill' && editingTemplate && (
          <TemplateFill
            template={editingTemplate}
            context={context}
            onSendJob={onSendJob}
            onUse={(text) => {
              onUse(editingTemplate, text);
              handleBackToList();
            }}
            onBack={handleBackToList}
          />
        )}

        {view === 'list' && (
          <>
            <div className={styles.header}>
              <div>
                <h3 className={styles.title}>Message Templates</h3>
                <p className={styles.subtitle}>You can create up to 10 templates.</p>
              </div>
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close templates"
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            <div className={styles.body}>
              {loading && <div className={styles.state}>Loading templates...</div>}

              {!loading && error && <div className={styles.error}>{error}</div>}

              {!loading && !error && templates.length === 0 && (
                <div className={styles.state}>No templates yet. Create one to get started.</div>
              )}

              {!loading && userTemplates.length > 0 && (
                <section className={styles.section} aria-labelledby={`${headingId}-own`}>
                  <div className={styles.sectionHead}>
                    <h4 id={`${headingId}-own`} className={styles.sectionTitle}>
                      Your templates
                    </h4>
                    <span className={styles.sectionCount}>{userTemplates.length} of 10</span>
                  </div>
                  {userTemplates.map((t) => renderCard(t, true))}
                </section>
              )}

              {!loading && systemTemplates.length > 0 && (
                <section className={styles.section} aria-labelledby={`${headingId}-system`}>
                  <div className={styles.sectionHead}>
                    <h4 id={`${headingId}-system`} className={styles.sectionTitle}>
                      Ready-made
                    </h4>
                    <span className={styles.sectionCount}>Copy one to edit it</span>
                  </div>
                  {systemTemplates.map((t) => renderCard(t, false))}
                </section>
              )}
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className={styles.createBtn}
                onClick={() => {
                  setFormError(null);
                  setDraft(null);
                  setView('create');
                }}
                disabled={!canCreateMore}
              >
                + Create New Template
              </button>
              {!canCreateMore && (
                <div className={styles.limitNote}>Maximum of 10 templates reached</div>
              )}
            </div>
          </>
        )}

        {deletingTemplate && (
          <TemplateDeleteConfirm
            templateName={deletingTemplate.name}
            onConfirm={handleDelete}
            onCancel={() => setDeletingTemplate(null)}
          />
        )}
      </div>
    </div>
  );
}

function TemplateForm({
  mode,
  initialName = '',
  initialTemplate = '',
  saving,
  error,
  onSave,
  onCancel,
}: {
  mode: 'create' | 'edit';
  initialName?: string;
  initialTemplate?: string;
  saving: boolean;
  error: string | null;
  onSave: (name: string, template: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [template, setTemplate] = useState(initialTemplate);
  const [nameError, setNameError] = useState('');
  const [templateError, setTemplateError] = useState('');

  const validate = (): boolean => {
    let valid = true;
    setNameError('');
    setTemplateError('');

    if (!name.trim()) {
      setNameError('Template name is required');
      valid = false;
    } else if (name.length > NAME_MAX) {
      setNameError(`Name must be ${NAME_MAX} characters or less`);
      valid = false;
    }

    if (!template.trim()) {
      setTemplateError('Message body is required');
      valid = false;
    } else if (template.length > TEMPLATE_MAX) {
      setTemplateError(`Message must be ${TEMPLATE_MAX} characters or less`);
      valid = false;
    }

    return valid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSave(name.trim(), template.trim());
  };

  const title = mode === 'create' ? 'Create Message Template' : 'Edit Message Template';

  return (
    <div className={styles.form}>
      <div className={styles.formHeader}>
        <h3 className={styles.title}>{title}</h3>
        <button
          type="button"
          className={styles.close}
          onClick={onCancel}
          aria-label="Close templates"
        >
          <Icon name="close" size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className={styles.formBody}>
        <div className={styles.field}>
          <label htmlFor="template-name" className={styles.fieldLabel}>
            Template Name
          </label>
          <input
            id="template-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Greeting"
            maxLength={NAME_MAX}
            className={`${styles.input} ${nameError ? styles.inputError : ''}`}
          />
          {nameError && <span className={styles.fieldError}>{nameError}</span>}
        </div>

        <div className={styles.field}>
          <label htmlFor="template-body" className={styles.fieldLabel}>
            Message Body
          </label>
          <textarea
            id="template-body"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Enter your message template..."
            maxLength={TEMPLATE_MAX}
            rows={8}
            className={`${styles.textarea} ${templateError ? styles.inputError : ''}`}
          />
          <div className={styles.fieldFooter}>
            {templateError && <span className={styles.fieldError}>{templateError}</span>}
            <span className={styles.charCount}>
              {template.length}/{TEMPLATE_MAX}
            </span>
          </div>
        </div>

        {error && <div className={styles.formError}>{error}</div>}

        <div className={styles.formActions}>
          <Button variant="ghost" type="button" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** "Name (copy)", then "(copy 2)"…: names are unique per company, and the cap is NAME_MAX. */
function copyName(name: string, own: TemplateDto[]): string {
  const taken = new Set(own.map((t) => t.name.toLowerCase()));
  for (let n = 1; ; n++) {
    const suffix = n === 1 ? ' (copy)' : ` (copy ${n})`;
    const candidate = name.slice(0, NAME_MAX - suffix.length) + suffix;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** Three lines, then a toggle — only when the text actually runs past them. */
function TemplatePreview({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !open) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);

  return (
    <>
      <div ref={ref} className={open ? styles.itemText : `${styles.itemText} ${styles.clamped}`}>
        &ldquo;{text}&rdquo;
      </div>
      {(overflows || open) && (
        <button
          type="button"
          className={styles.moreBtn}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
}

/** The one step between Use and the composer: pick whatever the chat cannot fill itself. */
function TemplateFill({
  template,
  context,
  onSendJob,
  onUse,
  onBack,
}: {
  template: TemplateDto;
  context: FillContext;
  onSendJob: ((jobId: string) => Promise<void>) | undefined;
  onUse: (text: string) => void;
  onBack: () => void;
}) {
  const needs = templateNeeds(template.template, context);
  const wantsJob = needs.job || needs.otherJob;
  const sendsCard = needs.otherJob && onSendJob !== undefined;
  const [jobs, setJobs] = useState<ActiveJobDto[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [topic, setTopic] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    if (!wantsJob) return;
    let live = true;
    getActiveJobs()
      .then((rows) => live && setJobs(rows))
      .catch(() => live && setLoadError(true));
    return () => {
      live = false;
    };
  }, [wantsJob]);

  const job = jobs?.find((j) => j.id === jobId);
  const text = fillTemplate(template.template, context, {
    ...(job && needs.job ? { jobTitle: job.title } : {}),
    ...(job && needs.otherJob ? { otherJobTitle: job.title } : {}),
    ...(topic ? { topic } : {}),
  });
  const ready = (!wantsJob || job !== undefined) && (!needs.topic || topic !== null);

  const use = async () => {
    if (!ready) return;
    if (sendsCard && job) {
      setSending(true);
      setSendError(null);
      try {
        await onSendJob(job.id);
      } catch {
        setSendError('Could not send the job card. Nothing was sent — try again.');
        setSending(false);
        return;
      }
    }
    onUse(text);
  };

  return (
    <div className={styles.form}>
      <div className={styles.formHeader}>
        <h3 className={styles.title}>{template.name}</h3>
        <button
          type="button"
          className={styles.close}
          onClick={onBack}
          aria-label="Back to templates"
        >
          <Icon name="close" size={20} />
        </button>
      </div>

      <div className={styles.formBody}>
        {wantsJob && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {needs.otherJob ? 'Which role do you want to suggest?' : 'Which job is this about?'}
            </span>
            {loadError ? (
              <span className={styles.fieldError}>
                Couldn't load your jobs. Try again in a moment.
              </span>
            ) : jobs === null ? (
              <span className={styles.hint}>Loading your jobs…</span>
            ) : jobs.length === 0 ? (
              <span className={styles.hint}>You have no active jobs.</span>
            ) : (
              <div className={styles.choices} role="radiogroup">
                {jobs.map((j) => (
                  <button
                    key={j.id}
                    type="button"
                    role="radio"
                    aria-checked={j.id === jobId}
                    className={j.id === jobId ? styles.choiceOn : styles.choice}
                    onClick={() => setJobId(j.id)}
                  >
                    {j.title}
                    {j.location && <span className={styles.choiceMeta}> · {j.location}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {needs.topic && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>What do you want to know?</span>
            <div className={styles.chips} role="radiogroup">
              {INFO_TOPICS.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={t === topic}
                  className={t === topic ? styles.choiceOn : styles.choice}
                  onClick={() => setTopic(t)}
                >
                  {t[0]!.toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}

        {ready && <div className={styles.itemText}>&ldquo;{text}&rdquo;</div>}
        {sendsCard && ready && (
          <span className={styles.hint}>
            The job card is sent now; your message waits in the box.
          </span>
        )}
        {sendError && <div className={styles.formError}>{sendError}</div>}

        <div className={styles.formActions}>
          <Button variant="ghost" type="button" onClick={onBack} disabled={sending}>
            Back
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => void use()}
            disabled={!ready || sending}
          >
            {sending ? 'Sending...' : sendsCard ? 'Send job card & use' : 'Use Template'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TemplateDeleteConfirm({
  templateName,
  onConfirm,
  onCancel,
}: {
  templateName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.deleteOverlay}>
      <div className={styles.deleteDialog}>
        <h4 className={styles.deleteTitle}>Delete Template</h4>
        <p className={styles.deleteText}>
          Are you sure you want to delete &ldquo;<strong>{templateName}</strong>&rdquo;? This action
          cannot be undone.
        </p>
        <div className={styles.deleteActions}>
          <Button variant="ghost" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" type="button" onClick={onConfirm}>
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
